import '@fontsource/barlow-condensed/latin-700.css';
import '@fontsource/barlow-condensed/latin-800-italic.css';
import '@fontsource/barlow-condensed/latin-900.css';
import '@fontsource/barlow-condensed/latin-900-italic.css';
import './style.css';
import './ui/ui.css';
import { enablePortraitLock } from './app/orientation';
import { mountDebugOverlay } from './app/debug';
import { loadBrand, loadBrandImage, loadBrandSvg } from './brand/loadBrand';
import { type AthleteModel, type AthleteVariant, loadAthleteModel } from './game/athleteModel';
import { createGame, type GameDebug } from './game/game';
import type { SceneStats } from './game/scene';
import { tunables } from './sim/tunables';

declare global {
  interface Window {
    /** Read by the Playwright smoke test. */
    __gridiron?: { ready: boolean; stats?: SceneStats; game?: GameDebug; error?: string; athletes?: 'rigged' | 'primitive' };
  }
}

/** Semantic UI tokens that are not brand colours (icon states, sizes) come from tunables. */
function applyUiTokens(): void {
  const root = document.documentElement.style;
  root.setProperty('--ui-open', tunables.look.iconOpen);
  root.setProperty('--ui-contested', tunables.look.iconContested);
  root.setProperty('--ui-covered', tunables.look.iconCovered);
  root.setProperty('--ui-icon-size', `${tunables.ui.iconSizePx}px`);
}

async function start(): Promise<void> {
  window.__gridiron = { ready: false };
  const container = document.getElementById('game');
  const uiRoot = document.getElementById('ui');
  if (!container || !uiRoot) throw new Error('#game or #ui element missing');

  const params = new URLSearchParams(location.search);
  // The rigged players load alongside the brand; if they fail or are slow, the game uses the primitive players.
  const athleteLoad = params.has('capsules') ? Promise.resolve(null) : loadAthletes(athleteVariant(params));
  const brand = await loadBrand();
  applyUiTokens();
  const [logo, mark, titleHero, titleLogo] = await Promise.all([
    loadBrandImage(brand, brand.logo.static).catch(() => null),
    loadBrandImage(brand, brand.logo.mark).catch(() => null),
    brand.title?.hero ? loadBrandImage(brand, brand.title.hero).catch(() => null) : Promise.resolve(null),
    loadBrandSvg(brand, brand.logo.animatedSvg ?? brand.logo.static),
  ]);
  // Touchdown clips are fetched one at a time by the big screen, as each comes up.
  const clips = brand.celebrations ?? [];
  const celebrationClips = {
    count: clips.length,
    load: (i: number) => {
      const c = clips[i]!;
      return loadBrandImage(brand, c.sheet)
        .then((image) => ({ image, frames: c.frames, cols: c.cols, fps: c.fps }))
        .catch(() => null);
    },
  };
  // Generated textures (numbers, boards) draw text, so the display font must be ready first.
  await Promise.all(
    ['900 64px "Barlow Condensed"', 'italic 800 32px "Barlow Condensed"'].map((f) => document.fonts.load(f).catch(() => [])),
  );
  const speed = Number(params.get('speed'));
  const athleteModel = await athleteLoad;
  const game = createGame({
    container,
    uiRoot,
    brand,
    logo,
    mark,
    titleHero,
    titleLogo,
    celebrationClips,
    tunables,
    // ?seed=abc replays a session exactly; otherwise every visit is new.
    matchSeed: params.get('seed') ?? `${Date.now()}`,
    timeScale: speed > 0 ? speed : 1,
    skipTitle: params.has('notitle'),
    resetGuide: params.has('guide'),
    athleteModel,
  });

  enablePortraitLock(document.body);
  mountDebugOverlay(game.stats);
  window.__gridiron = { ready: true, stats: game.stats, game: game.debug, athletes: game.athletes };
}

/** ?hd or ?standard picks the players; otherwise the build's VITE_PLAYERS (the artifact build sets hd), else standard. */
function athleteVariant(params: URLSearchParams): AthleteVariant {
  if (params.has('hd')) return 'hd';
  if (params.has('standard')) return 'standard';
  return import.meta.env.VITE_PLAYERS === 'hd' ? 'hd' : 'standard';
}

function loadAthletes(variant: AthleteVariant): Promise<AthleteModel | null> {
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), tunables.athlete.loadTimeoutSeconds * 1000));
  const files = variant === 'hd' ? tunables.athlete.hd : tunables.athlete;
  const load = loadAthleteModel(files, variant).catch((err: unknown) => {
    console.warn('Rigged athletes unavailable, using primitives:', err);
    return null;
  });
  return Promise.race([load, timeout]);
}

start().catch((err: unknown) => {
  const message = err instanceof Error ? err.message : String(err);
  window.__gridiron = { ready: false, error: message };
  const el = document.getElementById('fatal');
  if (el) {
    el.textContent = `Could not start: ${message}`;
    el.hidden = false;
  }
  console.error(err);
});
